import React from 'react';
import { 
  ShieldCheckIcon,
  SparklesIcon,
  UserIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { UserRole } from '../types';

interface UserRoleIndicatorProps {
  showDetails?: boolean;
  className?: string;
}

export const UserRoleIndicator: React.FC<UserRoleIndicatorProps> = ({
  showDetails = false,
  className = ''
}) => {
  const { user } = useAuth();

  if (!user) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="flex items-center space-x-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-md">
          <UserIcon className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-600 dark:text-gray-400">Guest</span>
        </div>
      </div>
    );
  }

  const rolePresentation = {
    [UserRole.GUEST]: {
      label: 'Guest',
      className: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600',
      Icon: UserIcon,
    },
    [UserRole.USER]: {
      label: 'User',
      className: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700',
      Icon: UserIcon,
    },
    [UserRole.PAID]: {
      label: 'Paid',
      className: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-700',
      Icon: SparklesIcon,
    },
    [UserRole.ADMIN]: {
      label: 'Admin',
      className: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 border-purple-200 dark:border-purple-700',
      Icon: ShieldCheckIcon,
    },
  }[user.role] ?? {
    label: user.role,
    className: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600',
    Icon: UserIcon,
  };

  const RoleIcon = rolePresentation.Icon;

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className={`flex items-center space-x-1 px-2 py-1 rounded-md border ${rolePresentation.className}`}>
        <RoleIcon className="h-4 w-4" />
        <span className="text-xs font-medium">{rolePresentation.label}</span>
      </div>
      
      {showDetails && (
        <div className="space-y-2 text-sm">
          <div className="text-gray-600 dark:text-gray-400">
            Signed in as {user.email}
          </div>
          
          <div className="space-y-1">
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Available Features</h4>
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <CheckIcon className="h-3 w-3 text-green-500" />
                <span className="text-xs text-gray-600 dark:text-gray-400">Gemini AI Analysis</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
